import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { CustomerNameInput } from './CustomerPicker';
import './UploadReceipt.css';

const DEFAULT_RECEIPT_SUMMARY = 'Bewirtung von Speisen und Getränken';
const DRAFT_EXPIRED_MESSAGE =
  'This draft has expired, open it again from Castle Club';

function localIsoDate() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

const BUYER_IDENTIFICATION_WARNING_PREFIX =
  'Rechnungsempfänger ohne USt-IdNr und ohne Adresse';

function isBlockingVatWarning(warning) {
  return !String(warning || '').startsWith(BUYER_IDENTIFICATION_WARNING_PREFIX);
}

function mapPaymentMethod(raw) {
  if (!raw) return 'bar';
  const lower = String(raw).toLowerCase();
  if (/(card|karte|visa|master|credit|debit|ec)/.test(lower)) return 'card';
  if (/(cash|bar|bargeld)/.test(lower)) return 'bar';
  return 'bar';
}

function buildInvoiceFromExtracted(extracted) {
  const receiptDate = extracted.delivery_date || extracted.date || '';
  const today = localIsoDate();
  return {
    invoice_date: extracted.invoice_date || today,
    delivery_date: receiptDate || today,
    customer_name: extracted.customer_name || '',
    customer_address: extracted.customer_address || '',
    customer_vat_id: extracted.customer_vat_id || '',
    items: extracted.items || [],
    net_amount: extracted.net_amount || 0,
    vat_rate: extracted.vat_rate ?? 0,
    vat_amount: extracted.vat_amount || 0,
    vat_details: extracted.vat_details || [],
    gross_amount: extracted.gross_amount || extracted.total_amount || 0,
    tip_amount: extracted.tip_amount || 0,
    receipt_number: extracted.receipt_number || '',
    use_summary_description:
      extracted.use_summary_description !== undefined
        ? !!extracted.use_summary_description
        : true,
    summary_description: extracted.summary_description || DEFAULT_RECEIPT_SUMMARY,
    vat_warnings: extracted.vat_warnings || [],
    already_paid: !!extracted.already_paid,
    paid_date: extracted.paid_date || receiptDate || today,
    payment_method: mapPaymentMethod(extracted.payment_method),
  };
}

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function buildVatDisplay(invoice) {
  const details = invoice.vat_details || [];
  const rows = details
    .slice()
    .sort((a, b) => a.rate - b.rate)
    .map((v) => ({
      rate: v.rate,
      rateLabel: `${v.rate}%`,
      net: roundMoney(v.net_amount),
      vat: roundMoney(v.vat_amount),
      gross: roundMoney(v.gross_amount ?? v.net_amount + v.vat_amount),
    }));

  const totals = {
    net: roundMoney(rows.reduce((s, r) => s + r.net, 0)),
    vat: roundMoney(rows.reduce((s, r) => s + r.vat, 0)),
    gross: roundMoney(rows.reduce((s, r) => s + r.gross, 0)),
  };

  const tip = roundMoney(invoice.tip_amount || 0);
  const totalPayable = roundMoney(totals.gross + tip);

  return { rows, totals, tip, totalPayable };
}

function recalculateVatFromItems(items) {
  const groups = {};
  const warnings = [];

  items.forEach((item) => {
    const rate = item.vat_rate;
    if (rate === undefined || rate === null) {
      warnings.push(`Kein Steuersatz für: ${(item.description || 'Artikel').slice(0, 50)}`);
      return;
    }
    const gross = roundMoney(item.total_price || 0);
    if (!gross) return;
    if (!groups[rate]) groups[rate] = { net: 0, vat: 0, gross: 0 };
    const net = roundMoney(gross / (1 + rate / 100));
    const vat = roundMoney(gross - net);
    groups[rate].net += net;
    groups[rate].vat += vat;
    groups[rate].gross += gross;
  });

  const vat_details = Object.keys(groups)
    .map(Number)
    .sort((a, b) => a - b)
    .map((rate) => ({
      rate,
      net_amount: roundMoney(groups[rate].net),
      vat_amount: roundMoney(groups[rate].vat),
      gross_amount: roundMoney(groups[rate].gross),
    }));

  const gross_amount = roundMoney(vat_details.reduce((s, v) => s + v.gross_amount, 0));

  return {
    vat_details,
    net_amount: roundMoney(vat_details.reduce((s, v) => s + v.net_amount, 0)),
    vat_amount: roundMoney(vat_details.reduce((s, v) => s + v.vat_amount, 0)),
    gross_amount,
    vat_rate: vat_details.length === 1 ? vat_details[0].rate : 0,
    vat_warnings: warnings,
  };
}

function UploadReceipt() {
  const [file, setFile] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [vatWarnings, setVatWarnings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [saveAsCustomer, setSaveAsCustomer] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [duplicateReceipt, setDuplicateReceipt] = useState(false);
  const [existingInvoices, setExistingInvoices] = useState([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftParam = searchParams.get('draft');

  useEffect(() => {
    api.get('/api/customers')
      .then((response) => setCustomers(response.data.customers || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!draftParam) return;

    let mounted = true;
    async function loadDraft() {
      setDraftLoading(true);
      setError('');
      try {
        const response = await api.get(`/api/drafts/${draftParam}`);
        if (!mounted) return;
        const extracted = response.data.extracted_data || {};
        setDraftId(response.data.draft_id || draftParam);
        setExtractedData(extracted);
        setVatWarnings(extracted.vat_warnings || []);
        setInvoiceData(buildInvoiceFromExtracted(extracted));
        setDuplicateReceipt(!!response.data.duplicate_receipt);
        setExistingInvoices(response.data.existing_invoices || []);
      } catch (err) {
        if (!mounted) return;
        const code = err.response?.data?.code;
        const message =
          code === 'draft_expired' || code === 'draft_not_found'
            ? DRAFT_EXPIRED_MESSAGE
            : err.response?.data?.error || DRAFT_EXPIRED_MESSAGE;
        setError(message);
        setExtractedData(null);
        setInvoiceData(null);
        setDraftId(null);
      } finally {
        if (mounted) setDraftLoading(false);
      }
    }

    loadDraft();
    return () => {
      mounted = false;
    };
  }, [draftParam]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type === 'application/pdf' || selectedFile.type === 'text/plain') {
        setFile(selectedFile);
        setError('');
      } else {
        setError('Please select a PDF or TXT file');
        setFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/api/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const extracted = response.data.extracted_data;
      setDraftId(null);
      setDuplicateReceipt(false);
      setExistingInvoices([]);
      setExtractedData(extracted);
      setVatWarnings(extracted.vat_warnings || []);
      setInvoiceData(buildInvoiceFromExtracted(extracted));
    } catch (err) {
      setError(err.response?.data?.error || 'Error uploading file');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'customer_name' || field === 'customer_address' || field === 'customer_vat_id') {
      setSelectedCustomerId('');
    }
    setInvoiceData({
      ...invoiceData,
      [field]: value
    });
  };

  const applyCustomer = (id, customer) => {
    setSelectedCustomerId(id);
    if (customer) {
      setSaveAsCustomer(false);
      setInvoiceData({
        ...invoiceData,
        customer_name: customer.name || '',
        customer_address: customer.address || '',
        customer_vat_id: customer.vat_id || '',
      });
    }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...invoiceData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };

    if (field === 'quantity' || field === 'unit_price') {
      updatedItems[index].total_price =
        parseFloat(updatedItems[index].quantity || 0) *
        parseFloat(updatedItems[index].unit_price || 0);
    }

    const recalculated = recalculateVatFromItems(updatedItems);
    const mergedWarnings = [
      ...(invoiceData.vat_warnings || []).filter(
        (w) => !w.startsWith('Kein Steuersatz für')
      ),
      ...(recalculated.vat_warnings || []),
    ];

    setInvoiceData({
      ...invoiceData,
      items: updatedItems,
      ...recalculated,
      vat_warnings: mergedWarnings,
    });
    setVatWarnings(mergedWarnings);
  };

  const handleCreateInvoice = async () => {
    if (!invoiceData.customer_name || !invoiceData.customer_address) {
      setError('Please fill in customer name and address');
      return;
    }

    const warnings = invoiceData.vat_warnings || vatWarnings;
    const blockingWarnings = warnings.filter(isBlockingVatWarning);
    if (blockingWarnings.length > 0) {
      setError(
        `Steuerprüfung: ${blockingWarnings.join(' ')} Bitte korrigieren Sie die Daten vor dem Erstellen.`
      );
      return;
    }

    if (!invoiceData.vat_details || invoiceData.vat_details.length === 0) {
      setError(
        'Steuerübersicht konnte nicht erstellt werden. Bitte MwSt.-Sätze am Beleg prüfen.'
      );
      return;
    }

    setLoading(true);
    setError('');

    try {
      const summaryDescription = invoiceData.use_summary_description
        ? (invoiceData.summary_description || '').trim() || DEFAULT_RECEIPT_SUMMARY
        : '';

      const payload = {
        ...invoiceData,
        summary_description: summaryDescription,
        customer_vat_id: (invoiceData.customer_vat_id || '').trim() || undefined,
        already_paid: !!invoiceData.already_paid,
        paid_date: invoiceData.already_paid ? invoiceData.paid_date : null,
        payment_method: invoiceData.already_paid ? invoiceData.payment_method : null,
        invoice_number: invoiceNumber ? parseInt(invoiceNumber) : undefined,
        draft_id: draftId || undefined,
      };

      await api.post('/api/invoices', payload);

      if (saveAsCustomer && !selectedCustomerId) {
        try {
          await api.post('/api/customers', {
            name: invoiceData.customer_name,
            address: invoiceData.customer_address,
            vat_id: invoiceData.customer_vat_id,
          });
        } catch (saveErr) {
          alert(saveErr.response?.data?.error || 'Rechnung erstellt, Kunde konnte aber nicht gespeichert werden.');
        }
      }

      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Error creating invoice');
    } finally {
      setLoading(false);
    }
  };

  const vatDisplay = invoiceData ? buildVatDisplay(invoiceData) : null;
  const displayWarnings = invoiceData
    ? (invoiceData.vat_warnings || vatWarnings).filter((warning) => {
        if (!isBlockingVatWarning(warning)) {
          const hasVat = (invoiceData.customer_vat_id || '').trim();
          const hasAddr = (invoiceData.customer_address || '').trim();
          return !hasVat && !hasAddr;
        }
        return true;
      })
    : [];

  const pageTitle = draftParam ? 'Create Invoice' : 'Upload Receipt';

  return (
    <div>
      <header className="header">
        <div className="header-content">
          <h1>{pageTitle}</h1>
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </button>
        </div>
      </header>

      <div className="container">
        {draftParam && draftLoading ? (
          <div className="card">
            <p>Loading receipt draft…</p>
          </div>
        ) : draftParam && !extractedData ? (
          <div className="card">
            <h2>Draft unavailable</h2>
            <div className="error">{error || DRAFT_EXPIRED_MESSAGE}</div>
            <button
              className="btn btn-secondary"
              style={{ marginTop: '1rem' }}
              onClick={() => navigate('/dashboard')}
            >
              Back to Dashboard
            </button>
          </div>
        ) : !extractedData ? (
          <div className="card">
            <h2>Upload Receipt File</h2>
            <p>Please upload a PDF or TXT file containing the receipt information.</p>

            <div className="upload-area">
              <input
                type="file"
                accept=".pdf,.txt"
                onChange={handleFileChange}
                className="file-input"
              />
              {file && (
                <div className="file-info">
                  <p>Selected file: {file.name}</p>
                  <p>Size: {(file.size / 1024).toFixed(2)} KB</p>
                </div>
              )}
            </div>

            {error && <div className="error">{error}</div>}

            <button
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={!file || loading}
            >
              {loading ? 'Processing...' : 'Upload and Extract Data'}
            </button>
          </div>
        ) : (
          <div className="card">
            <h2>Review and Edit Invoice Information</h2>
            {draftParam && (
              <p style={{ color: '#666', marginBottom: '1rem' }}>
                Prefilled from Castle Club / Lightspeed. Add the recipient, review, then create.
              </p>
            )}
            {duplicateReceipt && (
              <div className="warning" style={{ marginBottom: '1rem' }}>
                Warning: An invoice already exists for receipt{' '}
                <strong>{invoiceData?.receipt_number}</strong>
                {existingInvoices.length > 0 && (
                  <>
                    {' '}
                    (#{existingInvoices.map((inv) => inv.invoice_number).join(', #')})
                  </>
                )}
                . You can still create another invoice if needed.
              </div>
            )}

            <div className="form-section">
              <h3>Invoice Details</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="label">Invoice Number (optional)</label>
                  <input
                    type="number"
                    className="input"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder="Auto-generated if not provided"
                  />
                </div>
                <div className="form-group">
                  <label className="label">Rechnungsdatum</label>
                  <input
                    type="date"
                    className="input"
                    value={invoiceData.invoice_date}
                    onChange={(e) => handleInputChange('invoice_date', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="label">Leistungsdatum</label>
                  <input
                    type="date"
                    className="input"
                    value={invoiceData.delivery_date}
                    onChange={(e) => handleInputChange('delivery_date', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Customer Information</h3>
              <CustomerNameInput
                id="upload-receipt-customer-name"
                label="Customer Name *"
                value={invoiceData.customer_name}
                customers={customers}
                required
                onChange={(name) => handleInputChange('customer_name', name)}
                onSelectCustomer={applyCustomer}
              />
              <div className="form-group">
                <label className="label">Customer Address *</label>
                <textarea
                  className="input"
                  rows="3"
                  value={invoiceData.customer_address}
                  onChange={(e) => handleInputChange('customer_address', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="label">USt-IdNr (Rechnungsempfänger)</label>
                <input
                  type="text"
                  className="input"
                  value={invoiceData.customer_vat_id || ''}
                  onChange={(e) => handleInputChange('customer_vat_id', e.target.value)}
                  placeholder="z.B. DE123456789"
                />
              </div>
              {!selectedCustomerId && (
                <label className="checkbox-row save-customer-row">
                  <input
                    type="checkbox"
                    checked={saveAsCustomer}
                    onChange={(e) => setSaveAsCustomer(e.target.checked)}
                  />
                  <span>Diesen Empfänger als Kunden speichern</span>
                </label>
              )}
            </div>

            <div className="form-section">
              <h3>Leistungsbeschreibung</h3>
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={!!invoiceData.use_summary_description}
                  onChange={(e) => handleInputChange('use_summary_description', e.target.checked)}
                />
                <span>Nur Sammelbeschreibung statt einzelner Artikel verwenden</span>
              </label>

              {invoiceData.use_summary_description ? (
                <div className="form-group" style={{ marginTop: '12px' }}>
                  <label className="label">Text auf der Rechnung</label>
                  <input
                    type="text"
                    className="input"
                    value={invoiceData.summary_description || ''}
                    onChange={(e) => handleInputChange('summary_description', e.target.value)}
                    placeholder={DEFAULT_RECEIPT_SUMMARY}
                  />
                </div>
              ) : (
                <>
                  <h3>Items</h3>
                  {invoiceData.items.map((item, index) => (
                    <div key={index} className="item-row">
                      <input
                        type="number"
                        className="input-small"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      />
                      <input
                        type="text"
                        className="input-medium"
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      />
                      <input
                        type="number"
                        step="0.01"
                        className="input-small"
                        placeholder="Unit Price"
                        value={item.unit_price}
                        onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                      />
                      <span className="item-total">€ {item.total_price?.toFixed(2) || '0.00'}</span>
                    </div>
                  ))}
                </>
              )}
            </div>

            {displayWarnings.length > 0 && (
              <div className="warning" style={{ marginBottom: '16px' }}>
                <strong>Steuerhinweise:</strong>
                <ul style={{ margin: '8px 0 0 18px' }}>
                  {displayWarnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="form-section">
              <h3>Steuerübersicht</h3>
              {vatDisplay && (
                <>
                  <table className="vat-breakdown-table">
                    <thead>
                      <tr>
                        <th>Steuersatz</th>
                        <th className="text-right">Netto</th>
                        <th className="text-right">MwSt.</th>
                        <th className="text-right">Brutto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vatDisplay.rows.map((row) => (
                        <tr key={row.rate}>
                          <td>{row.rateLabel}</td>
                          <td className="text-right">€ {row.net.toFixed(2)}</td>
                          <td className="text-right">€ {row.vat.toFixed(2)}</td>
                          <td className="text-right">€ {row.gross.toFixed(2)}</td>
                        </tr>
                      ))}
                      <tr className="vat-total-row">
                        <td><strong>Gesamt</strong></td>
                        <td className="text-right"><strong>€ {vatDisplay.totals.net.toFixed(2)}</strong></td>
                        <td className="text-right"><strong>€ {vatDisplay.totals.vat.toFixed(2)}</strong></td>
                        <td className="text-right"><strong>€ {vatDisplay.totals.gross.toFixed(2)}</strong></td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="totals" style={{ marginTop: '16px' }}>
                    <div className="total-row">
                      <span>Trinkgeld (Tip):</span>
                      <div className="input-with-symbol">
                        <span className="currency-symbol">€</span>
                        <input
                          type="number"
                          step="0.01"
                          className="input-small text-right"
                          value={invoiceData.tip_amount}
                          onChange={(e) => handleInputChange('tip_amount', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                    <div className="total-row total-row-final">
                      <span>Zahlbetrag (Total):</span>
                      <span>€ {vatDisplay.totalPayable.toFixed(2)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="form-section">
              <h3>Zahlungsstatus</h3>
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={!!invoiceData.already_paid}
                  onChange={(e) => handleInputChange('already_paid', e.target.checked)}
                />
                <span>Bereits bezahlt</span>
              </label>
              {invoiceData.already_paid && (
                <div className="form-row" style={{ marginTop: '12px' }}>
                  <div className="form-group">
                    <label className="label">Bezahlt am</label>
                    <input
                      type="date"
                      className="input"
                      value={invoiceData.paid_date || ''}
                      onChange={(e) => handleInputChange('paid_date', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="label">Zahlungsart</label>
                    <select
                      className="input"
                      value={invoiceData.payment_method || 'bar'}
                      onChange={(e) => handleInputChange('payment_method', e.target.value)}
                    >
                      <option value="bar">bar</option>
                      <option value="card">card</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {error && <div className="error">{error}</div>}

            <div className="form-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setExtractedData(null);
                  setInvoiceData(null);
                  setFile(null);
                  setVatWarnings([]);
                  setSelectedCustomerId('');
                  setSaveAsCustomer(false);
                }}
              >
                Start Over
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateInvoice}
                disabled={loading}
              >
                {loading ? 'Creating Invoice...' : 'Create Invoice'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UploadReceipt;
